"use client"

import { motion } from "framer-motion"
import { Section } from "@/components/layout/Section"
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react"
import Link from "next/link"
import Image from "next/image"
import { useCallback, useEffect, useState } from "react"
import useEmblaCarousel from "embla-carousel-react"
import Autoplay from "embla-carousel-autoplay"
import { getReferences } from "@/lib/cms/queries"
import type { Reference } from "@/types/database"
import { stripHTML } from "@/lib/utils"
import { getTranslations } from "@/lib/translations"
import { isValidLocale, type Locale } from "@/lib/i18n"

type ReferencesProps = {
  locale?: string
}

export function References({ locale }: ReferencesProps) {
  const [referencesData, setReferencesData] = useState<Reference[]>([])
  const [loading, setLoading] = useState(true)
  
  // Get current locale and translations
  const currentLocale: Locale = (locale && isValidLocale(locale)) ? locale : 'tr'
  const t = getTranslations(currentLocale)

  useEffect(() => {
    loadReferences()
  }, [currentLocale])

  const loadReferences = async () => {
    try {
      // Sadece published referansları getir - locale filtresi ile
      const refs = await getReferences('published', currentLocale)
      // Sadece öne çıkan (featured) referansları göster
      const featuredRefs = refs.filter(ref => ref.featured === true)
      setReferencesData(featuredRefs)
    } catch (error) {
      console.error("Error loading references:", error)
      // Hata durumunda boş array
      setReferencesData([])
    } finally {
      setLoading(false)
    }
  }

  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    align: "start",
    slidesToScroll: 1,
  }, [Autoplay({ delay: 4000, stopOnInteraction: false })])
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(false)

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback((emblaApi: any) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on("reInit", onSelect)
    emblaApi.on("select", onSelect)
  }, [emblaApi, onSelect])

  return (
    <Section className="relative bg-white py-12 sm:py-16 md:py-20 lg:py-24 xl:py-32 overflow-hidden" containerClassName="!px-0">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-white" suppressHydrationWarning />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" suppressHydrationWarning />
      
      {/* Header - Container içinde */}
      <div className="relative z-10 mb-8 sm:mb-12 md:mb-16 lg:mb-20 px-4 sm:px-6 lg:px-8" suppressHydrationWarning>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 sm:gap-6 md:gap-8" suppressHydrationWarning>
          <div className="flex-1" suppressHydrationWarning>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block mb-2 sm:mb-3 md:mb-4"
              suppressHydrationWarning
            >
              <span className="text-xs sm:text-sm font-medium text-[rgb(3,74,166)] uppercase tracking-wider">
                {t.references.badge}
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-bold tracking-tight text-slate-900 mb-3 sm:mb-4 md:mb-6 leading-tight font-mono"
              suppressHydrationWarning
            >
              {t.references.title}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-600 max-w-2xl leading-relaxed"
              suppressHydrationWarning
            >
              {t.references.description}
            </motion.p>
          </div>
        </div>
      </div>

      {/* Navigation Buttons - Top */}
      <div className="relative z-10 flex items-center mb-4 sm:mb-6 md:mb-8 px-4 sm:px-6 lg:px-8" suppressHydrationWarning>
        <div className="flex gap-2 sm:gap-3" suppressHydrationWarning>
          <button
            onClick={scrollPrev}
            disabled={prevBtnDisabled}
            className="p-2.5 sm:p-3 bg-slate-200/90 hover:bg-slate-300/90 active:bg-slate-400/90 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all touch-manipulation"
            aria-label={t.references.previous}
            suppressHydrationWarning
          >
            <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 text-black stroke-[2]" />
          </button>
          <button
            onClick={scrollNext}
            disabled={nextBtnDisabled}
            className="p-2.5 sm:p-3 bg-slate-200/90 hover:bg-slate-300/90 active:bg-slate-400/90 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all touch-manipulation"
            aria-label={t.references.next}
            suppressHydrationWarning
          >
            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 text-black stroke-[2]" />
          </button>
        </div>
      </div>

      {/* Carousel - Full Width */}
      <div className="relative z-10 w-full mb-6 sm:mb-8 md:mb-12 lg:mb-16 overflow-visible" suppressHydrationWarning>
        <div className="relative pl-4 sm:pl-6 lg:pl-8 pr-0" suppressHydrationWarning>
                 <div className="overflow-hidden w-full md:w-[calc(100%+25%)] lg:w-[calc(100%+12.5%)]" ref={emblaRef} suppressHydrationWarning>
                   <div className="flex gap-3 sm:gap-4" suppressHydrationWarning>
                     {loading ? (
                       <div className="flex-[0_0_100%] text-center py-8 sm:py-12 text-slate-500 text-sm sm:text-base" suppressHydrationWarning>{t.references.loading}</div>
                     ) : referencesData.length === 0 ? (
                       <div className="flex-[0_0_100%] text-center py-8 sm:py-12 text-slate-500 text-sm sm:text-base" suppressHydrationWarning>{t.references.empty}</div>
                     ) : (
                       referencesData.map((ref, index) => (
                <div
                  key={ref.id}
                  className="flex-[0_0_85%] sm:flex-[0_0_75%] md:flex-[0_0_50%] lg:flex-[0_0_28%] min-w-0 px-1 sm:px-2"
                  suppressHydrationWarning
                >
                  <div className="group relative overflow-hidden bg-white cursor-pointer h-full" suppressHydrationWarning>
                    <Link href={`/${currentLocale}/${currentLocale === 'tr' ? 'referanslar' : 'references'}/${ref.slug || ref.id}`} className="block h-full">
                      {/* Image Container */}
                      <div className="relative aspect-[4/3] w-full overflow-hidden" suppressHydrationWarning>
                        {(() => {
                          // If no image URL, show placeholder
                          if (!ref.image || ref.image.trim() === "") {
                            return (
                              <div className="w-full h-full bg-gradient-to-br from-slate-200 via-slate-100 to-slate-200 flex items-center justify-center">
                                <div className="text-center p-4">
                                  <div className="text-4xl mb-2">📷</div>
                                  <p className="text-xs text-slate-500 font-medium">{t.references.imagePlaceholder}</p>
                                </div>
                              </div>
                            );
                          }
                          
                          const isVideo = /\.(webm|mp4|mov|avi|mkv)$/i.test(ref.image);
                          if (isVideo) {
                            return (
                              <video
                                src={ref.image}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                muted
                                loop
                                playsInline
                                preload="none"
                              />
                            );
                          }
                          return (
                            <Image
                              src={ref.image}
                              alt={ref.title}
                              fill
                              className="object-cover transition-transform duration-700 group-hover:scale-110"
                              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                              loading="lazy"
                            />
                          );
                        })()}
                        
                        {/* Base Overlay - Always visible with site primary color */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[rgb(3,74,166)]/60 via-[rgb(3,74,166)]/40 to-[rgb(3,74,166)]/20 transition-opacity duration-500" suppressHydrationWarning />
                        
                        {/* Overlay on Hover - Darker site primary color */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[rgb(3,74,166)]/95 via-[rgb(3,74,166)]/70 to-[rgb(3,74,166)]/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500" suppressHydrationWarning />
                        
                        {/* Title - Always visible at bottom, hides on hover */}
                        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 md:p-6 bg-gradient-to-t from-[rgb(3,74,166)]/80 to-transparent group-hover:opacity-0 transition-opacity duration-500 z-10" suppressHydrationWarning>
                          <h3 className="text-base sm:text-lg md:text-xl font-bold text-white line-clamp-2">
                            {ref.title}
                          </h3>
                        </div>
                        
                        {/* Content Overlay - Shows on Hover/Touch */}
                        <div className="absolute inset-0 flex flex-col justify-end p-3 sm:p-4 md:p-6 text-white opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-500 z-20" suppressHydrationWarning>
                          <div className="space-y-2 sm:space-y-3 md:space-y-4" suppressHydrationWarning>
                            <h3 className="text-lg sm:text-xl md:text-2xl font-bold line-clamp-2">{ref.title}</h3>
                            <p className="text-xs sm:text-sm text-white/90 leading-relaxed line-clamp-2 sm:line-clamp-3">
                              {stripHTML(ref.description || '')}
                            </p>
                            <div className="flex items-center text-white font-medium transition-colors duration-300" suppressHydrationWarning>
                              <span className="text-xs sm:text-sm">{t.references.viewCaseStudy}</span>
                              <ArrowRight className="ml-2 h-3 w-3 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform duration-300" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
                       ))
                     )}
            </div>
          </div>
        </div>
      </div>

      {/* View All Button - Bottom */}
      <div className="relative z-10 flex justify-end items-center gap-2 text-slate-400 mt-6 sm:mt-8 md:mt-12 px-4 sm:px-6 lg:px-8" suppressHydrationWarning>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          suppressHydrationWarning
        >
          <Link href={`/${currentLocale}/${currentLocale === 'tr' ? 'referanslar' : 'references'}`} className="flex items-center gap-2 group hover:text-slate-600 active:text-slate-700 transition-colors touch-manipulation" suppressHydrationWarning>
            <span className="text-xs sm:text-sm font-medium">{t.references.viewAll}</span>
            <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </Section>
  )
}

